
import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import "./EvaAssessmentReport.css";


//========================================================
// EVA Assessment Completion Report
//========================================================

function compareAnswers(question, answer) {

    if (question.correct === undefined) {
        return null;
    }

    if (question.type === "multiple" ||
        question.type === "grid" ||
        question.type === "sequence") {

        const actual = Array.isArray(answer) ? [...answer] : [];
        const expected = [...question.correct];

        if (question.type !== "sequence") {
            actual.sort();
            expected.sort();
        }

        return (
            actual.length === expected.length &&
            actual.every((value, index) => value === expected[index])
        );
    }

    if (question.type === "hotspot") {

        if (!answer) {
            return false;
        }

        const dx = answer.x - question.target.x;
        const dy = answer.y - question.target.y;

        return Math.sqrt(dx * dx + dy * dy) <=
            question.target.radius;
    }

    return answer === question.correct;
}


//========================================================
// Format Answer
//========================================================

function formatAnswer(question, answer) {

    if (answer === undefined || answer === null) {
        if (question.type === "slider") {
            return "3 — Moderate (displayed default)";
        }

        if (question.type === "sequence") {
            return question.options
                .map(option => option.label)
                .join(" → ") + " (displayed default)";
        }

        return "No response";
    }

    if (question.type === "multiple") {
        return answer.join(", ");
    }

    if (question.type === "grid") {
        return answer
            .map(index => `Square ${index + 1}`)
            .join(", ");
    }

    if (question.type === "hotspot") {
        return (
            `X: ${answer.x.toFixed(1)}%, ` +
            `Y: ${answer.y.toFixed(1)}%`
        );
    }

    if (question.type === "floorplan") {

        return question.rooms.find(
            room => room.id === answer
        )?.label || answer;
    }

    if (question.type === "sequence") {

        return answer.map(id =>
            question.options.find(
                option => option.id === id
            )?.label || id
        ).join(" → ");
    }

    if (question.type === "evidence") {
        return answer.name;
    }

    if (question.type === "slider") {
        return (
            `${answer} — ` +
            question.labels[answer - question.min]
        );
    }

    return String(answer);
}


//========================================================
// Evidence Image Preview
//========================================================

function UploadedEvidence({ file }) {

    const [url, setUrl] = useState(null);

    useEffect(() => {

        if (!file || !(file instanceof Blob)) {
            setUrl(null);
            return;
        }

        const objectUrl = URL.createObjectURL(file);

        setUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };

    }, [file]);

    if (!url) {
        return null;
    }

    return (
        <img
            className="eva-report-upload-image"
            src={url}
            alt="Uploaded assessment evidence"
        />
    );

}


//========================================================
// Image Annotation
//========================================================

function ReportImage({ question, answer }) {

    const isGrid = question.type === "grid";
    const isHotspot = question.type === "hotspot";

    if (!isGrid && !isHotspot) {
        return null;
    }

    const size = question.gridSize || 4;

    return (

        <div className="eva-report-photo">

            <img
                src={question.image}
                alt={question.title}
            />

            {isGrid && (

                <div
                    className="eva-report-grid"
                    style={{
                        gridTemplateColumns: `repeat(${size}, 1fr)`,
                        gridTemplateRows: `repeat(${size}, 1fr)`
                    }}
                >

                    {Array.from(
                        { length: size * size },
                        (_, index) => index
                    ).map(index => (

                        <div
                            key={index}
                            className={
                                "eva-report-grid-cell " +
                                ((answer || []).includes(index)
                                    ? "selected"
                                    : "")
                            }
                        >
                            {(answer || []).includes(index)
                                ? "✓"
                                : ""}
                        </div>

                    ))}

                </div>

            )}

            {isHotspot && answer && (

                <span
                    className="eva-report-marker"
                    style={{
                        left: `${answer.x}%`,
                        top: `${answer.y}%`
                    }}
                >
                    +
                </span>

            )}

        </div>

    );

}


//========================================================
// Floor Plan Result
//========================================================

function ReportFloorplan({ question, answer }) {

    return (

        <div className="eva-report-floorplan">

            {question.rooms.map(room => (

                <div
                    key={room.id}
                    className={
                        "eva-report-room " +
                        (answer === room.id ? "selected" : "")
                    }
                    style={{
                        left: `${room.x}%`,
                        top: `${room.y}%`,
                        width: `${room.w}%`,
                        height: `${room.h}%`
                    }}
                >
                    {room.label}
                </div>

            ))}

        </div>

    );

}


//========================================================
// Main Report
//========================================================

export default function EvaAssessmentReport({
    questions,
    answers,
    onClose
}) {

    const [generatedAt] = useState(() => new Date());

    //----------------------------------------------------
    // Results
    //----------------------------------------------------

    const results = useMemo(() => {

        return questions.map(question => ({

            question,
            answer: answers[question.id],
            correct: compareAnswers(
                question,
                answers[question.id]
            )

        }));

    }, [questions, answers]);

    const scored = results.filter(
        result => result.correct !== null
    );

    const matched = scored.filter(
        result => result.correct === true
    ).length;

    const recorded = results.length - scored.length;

    const percentage = scored.length
        ? Math.round(matched / scored.length * 100)
        : 0;

    //----------------------------------------------------
    // Print
    //----------------------------------------------------

    const printReport = () => {
        window.print();
    };

    //----------------------------------------------------
    // Render
    //----------------------------------------------------

    return (

        <div className="eva-report-root">

            <div
                className="eva-report-backdrop"
                onClick={onClose}
            />

            <div
                className="eva-report-window"
                role="dialog"
                aria-modal="true"
                aria-label="Assessment completion report"
            >

                {/*===========================================
                  Screen Toolbar
                ===========================================*/}

                <div className="eva-report-toolbar">

                    <div>

                        <strong>
                            ASSESSMENT REPORT
                        </strong>

                        <span>
                            OmniScan Pro / Eva
                        </span>

                    </div>

                    <div className="eva-report-toolbar-actions">

                        <button
                            type="button"
                            onClick={printReport}
                        >
                            Print / Save PDF
                        </button>

                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close report"
                        >
                            ×
                        </button>

                    </div>

                </div>

                {/*===========================================
                  Printable Report
                ===========================================*/}

                <div className="eva-report-scroll">

                    <article className="eva-report-document">

                        <header className="eva-report-document-header">

                            <div className="eva-report-brand">

                                <span>
                                    OMNISCAN PRO
                                </span>

                                <strong>
                                    EVA
                                </strong>

                            </div>

                            <div className="eva-report-document-heading">

                                <div className="eva-report-eyebrow">
                                    INTERACTIVE WORKPLACE ASSESSMENT
                                </div>

                                <h1>
                                    Assessment Completion Report
                                </h1>

                                <p>
                                    Workplace Safety Training Demonstration
                                </p>

                            </div>

                            <div className="eva-report-metadata">

                                <div>
                                    <span>Generated</span>
                                    <strong>
                                        {generatedAt.toLocaleString("en-GB")}
                                    </strong>
                                </div>

                                <div>
                                    <span>Assessment type</span>
                                    <strong>
                                        Demonstration / Training
                                    </strong>
                                </div>

                                <div>
                                    <span>Assessment status</span>
                                    <strong>
                                        Completed
                                    </strong>
                                </div>

                            </div>

                        </header>

                        {/*===================================
                          Summary
                        ===================================*/}

                        <section className="eva-report-summary">

                            <h2>Assessment Overview</h2>

                            <div className="eva-report-stats">

                                <div className="eva-report-stat">

                                    <strong>
                                        {questions.length}
                                    </strong>

                                    <span>
                                        Questions completed
                                    </span>

                                </div>

                                <div className="eva-report-stat">

                                    <strong>
                                        {matched}/{scored.length}
                                    </strong>

                                    <span>
                                        Reference answers matched
                                    </span>

                                </div>

                                <div className="eva-report-stat">

                                    <strong>
                                        {percentage}%
                                    </strong>

                                    <span>
                                        Reference match rate
                                    </span>

                                </div>

                                <div className="eva-report-stat">

                                    <strong>
                                        {recorded}
                                    </strong>

                                    <span>
                                        Unscored observations
                                    </span>

                                </div>

                            </div>

                            <p className="eva-report-summary-text">

                                This assessment demonstrates the capture
                                of structured responses, photographic
                                evidence, visual hazard identification
                                and location-based observations.

                            </p>

                        </section>

                        {/*===================================
                          Individual Results
                        ===================================*/}

                        <section className="eva-report-results">

                            <h2>Detailed Assessment Results</h2>

                            {results.map((result, index) => {

                                const {
                                    question,
                                    answer,
                                    correct
                                } = result;

                                return (

                                    <div
                                        className="eva-report-result"
                                        key={question.id}
                                    >

                                        <div className="eva-report-result-header">

                                            <div>

                                                <small>
                                                    QUESTION{" "}
                                                    {String(index + 1).padStart(2, "0")}
                                                </small>

                                                <h3>
                                                    {question.title}
                                                </h3>

                                            </div>

                                            <span
                                                className={
                                                    "eva-report-result-status " +
                                                    (correct === true
                                                        ? "matched"
                                                        : correct === false
                                                            ? "review"
                                                            : "recorded")
                                                }
                                            >

                                                {correct === true
                                                    ? "MATCHED"
                                                    : correct === false
                                                        ? "REVIEW"
                                                        : "RECORDED"}

                                            </span>

                                        </div>

                                        <p className="eva-report-question">
                                            {question.question}
                                        </p>

                                        <div className="eva-report-answer">

                                            <span>
                                                RESPONSE
                                            </span>

                                            <strong>
                                                {formatAnswer(
                                                    question,
                                                    answer
                                                )}
                                            </strong>

                                        </div>

                                        {(question.type === "grid" ||
                                          question.type === "hotspot") && (

                                            <ReportImage
                                                question={question}
                                                answer={answer}
                                            />

                                        )}

                                        {question.type === "floorplan" && (

                                            <ReportFloorplan
                                                question={question}
                                                answer={answer}
                                            />

                                        )}

                                        {question.type === "evidence" &&
                                            answer && (

                                            <UploadedEvidence
                                                file={answer}
                                            />

                                        )}

                                        {question.correct !== undefined && (

                                            <div className="eva-report-reference">

                                                <strong>
                                                    Reference assessment
                                                </strong>

                                                <span>
                                                    {question.type === "hotspot"
                                                        ? "Predefined demonstration target"
                                                        : formatAnswer(
                                                            question,
                                                            question.correct
                                                        )}
                                                </span>

                                            </div>

                                        )}

                                        <p className="eva-report-explanation">
                                            {question.explanation}
                                        </p>

                                    </div>

                                );

                            })}

                        </section>

                        {/*===================================
                          Closing Notes
                        ===================================*/}

                        <footer className="eva-report-document-footer">

                            <strong>
                                Assessment Notes
                            </strong>

                            <p>
                                This report was generated from an
                                interactive OmniScan Pro / Eva
                                demonstration. Responses and images
                                are illustrative. No automated
                                determination of statutory compliance
                                has been performed.
                            </p>

                            <p>
                                This document is not a certified
                                workplace inspection, fire risk
                                assessment or training certificate.
                            </p>

                            <div className="eva-report-footer-brand">
                                OMNISCAN PRO / EVA
                            </div>

                        </footer>

                    </article>

                </div>

            </div>

        </div>

    );

}
